import { StaticCurrencyConfig } from '@/config/currency';
import instrumentConfig, { StaticInstrumentConfig } from '@/config/instruments';
import { useAppDispatch } from '@/state/hooks';
import {
    CurrencyConfigs,
    InstrumentConfigs,
    setCurrencyConfigs,
    setInstrumentConfigs
} from '@/state/reducers/ohoSlice';
import { CurrencyConfigResponse, InstrumentConfigResponse } from '@/types/sso';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UseLanguageReturn } from '../hooks/useLanguage';
import { Logger } from '../logger';

export interface OhoContext {
    instrumentConfigs: InstrumentConfigs;
    currencyConfigs: CurrencyConfigs;
}

export interface OhoProviderProps {
    children: React.ReactNode;
}

const ohoURL = window.config?.oho.rest;

const OhoContext = createContext({} as OhoContext);
export const useOho = () => useContext(OhoContext);
export const OhoProvider = (props: OhoProviderProps) => {
    const location = useLocation();

    const [instrumentConfigs, setInstrumentConfigsState] = useState<InstrumentConfigs>({
        Margin: {},
        Deliverable: {}
    });
    const [currencyConfigs, setCurrencyConfigsState] = useState<CurrencyConfigs>([]);

    const dispatch = useAppDispatch();

    const isLoginPage = useMemo(() => {
        return location.pathname === '/login';
    }, [location]);

    useEffect(() => {
        async function fetchInstruments() {
            try {
                Logger({
                    title: `Outbound: Fetching instruments from /TradingGuiSupport/Instruments`,
                    callback: () => {}
                });
                const marginResponse = await fetch(`${ohoURL}/instruments?deliverable=false`);
                const deliverableResponse = await fetch(`${ohoURL}/instruments?deliverable=true`);
                if (!marginResponse.ok || !deliverableResponse.ok) {
                    throw new Error('Failed to fetch instruments');
                }
                const marginData = await marginResponse.json();
                const deliverableData = await deliverableResponse.json();
                // check for overlapping symbols

                Logger({
                    title: `Inbound: Fetched instruments from /TradingGuiSupport/Instruments.`,
                    callback: () => {
                        console.log('Instruments Config:', { marginData, deliverableData });
                    }
                });

                const formattedData: InstrumentConfigs = {
                    Margin: {},
                    Deliverable: {}
                };
                marginData.forEach((item: InstrumentConfigResponse) => {
                    const [provider, netdania]: any = item.histPriceSymbol?.split(':') || ['', ''];
                    let symbol = item.symbol.replace('CFD/', '');
                    if (item.subType === 'Future') symbol = item.tsSymbol;
                    const type = translateType(item.subType);

                    formattedData['Margin'][symbol] = {
                        ...item,
                        type,
                        price_decimals: item.priceDecimals,
                        pip_size: item.pipsDecimals,
                        ccy2_enabled: ['FX', 'Crypto'].includes(item.type) && item.subType === 'Spot' ? 1 : 0,
                        ccy1: item.currency1,
                        ccy2: item.currency2,
                        netdania,
                        provider,
                        gcexSymbol: item.symbol,
                        tsSymbol: item.tsSymbol,
                        assetName: item.assetName,
                        product_type: instrumentConfig[symbol]?.product_type
                    };
                    if (item.subType === 'Future') {
                        formattedData['Margin'][item.assetName] = {
                            ...item,
                            type,
                            price_decimals: item.priceDecimals,
                            pip_size: item.pipsDecimals,
                            ccy2_enabled: 0,
                            ccy1: item.currency1,
                            ccy2: item.currency2,
                            netdania,
                            provider,
                            gcexSymbol: item.symbol,
                            tsSymbol: item.tsSymbol,
                            assetName: item.assetName,
                            product_type: instrumentConfig[symbol]?.product_type
                        };
                    }
                });
                deliverableData.forEach((item: InstrumentConfigResponse) => {
                    const [provider, netdania]: any = item.histPriceSymbol?.split(':') || ['', ''];
                    const symbol = item.symbol;
                    const type = translateType(item.type);

                    formattedData['Deliverable'][symbol] = {
                        ...item,
                        type,
                        price_decimals: item.priceDecimals,
                        pip_size: item.pipsDecimals,
                        ccy2_enabled: ['FX', 'Crypto'].includes(item.type) && item.subType === 'Spot' ? 1 : 0,
                        netdania,
                        provider,
                        gcexSymbol: item.symbol,
                        product_type: instrumentConfig[symbol]?.product_type
                    };
                });
                setInstrumentConfigsState(formattedData);
                dispatch(setInstrumentConfigs(formattedData));
            } catch (error: any) {
                Logger({
                    title: `Inbound: Oho Error - Message: ${error}.`,
                    callback: () => {}
                });
            }
        }
        async function fetchCurrencies() {
            try {
                Logger({
                    title: `Outbound: Fetching currencies from /TradingGuiSupport/Currencies`,
                    callback: () => {}
                });
                const response = await fetch(`${ohoURL}/currencies`);
                if (!response.ok) {
                    throw new Error('Failed to fetch currencies');
                }
                const data = await response.json();
                Logger({
                    title: `Inbound: Fetched currencies from /TradingGuiSupport/Currencies.`,
                    callback: () => {
                        console.log('Currencies Config:', data);
                    }
                });

                const formattedData: (CurrencyConfigResponse & StaticCurrencyConfig)[] = data.map(
                    (item: CurrencyConfigResponse) => {
                        return {
                            ...item,
                            celer: item.code,
                            netdania: item.code,
                            show: item.assetName || item.code,
                            min_order_size: item.minOrderSize,
                            order_decimals: item.decimals,
                            increments: item.quantityIncrements,
                            type: item.isCrypto ? 'Crypto' : 'Fiat',
                            longName: item.description
                        };
                    }
                );
                setCurrencyConfigsState(formattedData);
                dispatch(setCurrencyConfigs(formattedData));
            } catch (error: any) {
                Logger({
                    title: `Inbound: Oho Error - Message: ${error}.`,
                    callback: () => {}
                });
            }
        }

        if (isLoginPage) {
            fetchInstruments();
            fetchCurrencies();
        }
    }, [isLoginPage]);

    const value = useMemo<OhoContext>(() => {
        return { instrumentConfigs, currencyConfigs };
    }, [instrumentConfigs, currencyConfigs]);

    return <OhoContext.Provider value={value}>{props.children}</OhoContext.Provider>;
};

const translateType = (type: InstrumentConfigResponse['subType']): StaticInstrumentConfig['type'] => {
    switch (type) {
        case 'Spot':
            return 'FX';
        case 'Crypto':
        case 'Commodity':
        case 'Future':
            return type as StaticInstrumentConfig['type'];
        case 'EquityIndex':
            return 'Index';
        default:
            return 'FX';
    }
};

export const translateLangType = (type: StaticInstrumentConfig['type'], lang: UseLanguageReturn): string => {
    switch (type) {
        case 'FX':
            return lang.common.fxSpot;
        case 'Crypto':
            return lang.common.crypto;
        case 'Commodity':
            return lang.common.commCFD;
        case 'Future':
            return lang.common.futuresCFD;
        case 'Index':
            return lang.common.indexCFD;
        default:
            return '';
    }
};
